import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateProforma, useOrderInvoices } from '@/api/hooks'
import type { OrderDetail } from '@/api/types'
import { ErrorBanner, formatMoney, isoDate } from '@/components/common'

/**
 * Raises the proforma that has to go out before the tax invoice, from the order
 * the person closing the event is already looking at.
 */
export function ProformaDialog({
  order,
  onClose,
}: {
  order: OrderDetail | null
  onClose: () => void
}) {
  const navigate = useNavigate()
  const create = useCreateProforma()
  const { data: existing } = useOrderInvoices(order?.id)

  const [invoiceDate, setInvoiceDate] = useState(() => isoDate(0))
  const [validUntil, setValidUntil] = useState(() => isoDate(14))
  const [interState, setInterState] = useState(false)
  const [notes, setNotes] = useState('')

  if (!order) return null

  const alreadyIssued = (existing ?? []).some(
    (invoice) => invoice.invoiceType === 'PROFORMA' && invoice.status !== 'CANCELLED',
  )

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Proforma invoice — {order.orderNumber}</DialogTitle>
      <DialogContent>
        <ErrorBanner error={create.error} />

        {alreadyIssued ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This order already has a proforma. Raise another only if the figures have changed — the
            customer will be holding two different estimates until the tax invoice supersedes them.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            The same figures the tax invoice will carry, sent so the customer can approve them or
            arrange funds. It is not a demand for payment and does not count towards what they owe.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Proforma date"
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              type="date"
              label="Valid until"
              value={validUntil}
              onChange={(event) => setValidUntil(event.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="How long these figures stand"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch checked={interState} onChange={(event) => setInterState(event.target.checked)} />
              }
              label="Inter-state supply (IGST instead of CGST + SGST)"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
          </Grid>
        </Grid>

        <Alert severity="success" sx={{ mt: 2 }}>
          Order value {formatMoney(order.totalAmount)} · received {formatMoney(order.paidAmount)} ·
          balance {formatMoney(order.balanceAmount)}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(
              {
                orderId: order.id,
                invoiceDate,
                validUntil,
                interState,
                notes: notes || undefined,
              },
              {
                onSuccess: () => {
                  onClose()
                  navigate('/invoices')
                },
              },
            )
          }
        >
          Raise proforma
        </Button>
      </DialogActions>
    </Dialog>
  )
}
